import classNames from 'classnames';
import { useState } from 'react';
import { SpcResult, SpcResultsPos, SpcTogglesMap } from '../../../shared/types/spc.ts';
import { NUM_SPC_CHECKS, SPC_DESCRIPTIONS } from '../../../shared/utils/spcConstants.ts';
import { Protocol } from '../../types/sharedTypeImpl.ts';
import { SpcTogglesContextMenu, SpcTogglesContextMenuState } from './spcTogglesContextMenu.tsx';


const nullResultsBackground = '#00000028';

interface Props {
    style?: React.CSSProperties;
    protocol: Protocol;
    spcResults: SpcResultsPos[];
    activePositionIdx: number | null;
    setActivePositionIdx: (activePositionIdx: number | null) => void;
    spcTogglesMap: SpcTogglesMap;
    onSpcTogglesChange: (isEnabled: boolean, colIdx: number, positionNumber?: string) => void;
}

export function SpcTogglesTable({
    style,
    protocol,
    spcResults,
    activePositionIdx,
    setActivePositionIdx,
    spcTogglesMap,
    onSpcTogglesChange,
}: Props) {
    const [contextMenuState, setContextMenuState] = useState<SpcTogglesContextMenuState | null>(null);


    const handleHeaderRightClick = (e: React.MouseEvent, colIdx: number) => {
        e.preventDefault();
        setContextMenuState({
            checkIdx: colIdx,
            screenCoords: { x: e.clientX, y: e.clientY }
        });
    };


    return <>
        <table className='spc-checks-table' style={style}>
            <thead>
                <tr>
                    <th />
                    {Array.from({ length: NUM_SPC_CHECKS }, (_, index) =>
                        <th
                            key={index}
                            title={SPC_DESCRIPTIONS[index]}
                            onContextMenu={(e) => handleHeaderRightClick(e, index)}
                        >
                            {index + 1}
                        </th>
                    )}
                </tr>
            </thead>
            <tbody>
                {spcResults.map(({ position, aggregated }, index) =>
                    <SpcCheckRow
                        key={position.positionNumber}
                        index={index}
                        positionNumber={position.positionNumber}
                        isSelectedPosition={index === activePositionIdx}
                        positionChecks={aggregated}
                        enabledChecks={spcTogglesMap[position.positionNumber]}
                        onPositionSelect={setActivePositionIdx}
                        onSpcTogglesChange={onSpcTogglesChange}
                    />
                )}
            </tbody>
        </table>

        {contextMenuState && <SpcTogglesContextMenu
            screenCoords={contextMenuState.screenCoords}
            onEnableAllSelected={() => onSpcTogglesChange(true, contextMenuState.checkIdx)}
            onDisableAllSelected={() => onSpcTogglesChange(false, contextMenuState.checkIdx)}
            onClose={() => setContextMenuState(null)}
        />}
    </>;
}


function SpcCheckRow({
    positionNumber,
    index,
    isSelectedPosition,
    positionChecks,
    enabledChecks,
    onSpcTogglesChange,
    onPositionSelect,
}: {
    positionNumber: string,
    index: number,
    isSelectedPosition: boolean,
    positionChecks: SpcResult,
    enabledChecks: boolean[],
    onPositionSelect: (positionIdx: number) => void,
    onSpcTogglesChange: (isEnabled: boolean, colIdx: number, positionNumber?: string) => void,
}) {

    const allChecksNull = positionChecks.every(check => check === null);

    return (
        <tr
            className={classNames({ 'highlight--selected': isSelectedPosition })}
            onClick={() => onPositionSelect(index)}
        >
            <td style={{ backgroundColor: allChecksNull ? nullResultsBackground : undefined }}>
                {positionNumber}
            </td>
            {positionChecks.map((spcCheckResult, colIdx) =>
                <SpcCheckCell
                    key={colIdx}
                    positionNumber={positionNumber}
                    colIdx={colIdx}
                    spcCheckResult={spcCheckResult}
                    title={SPC_DESCRIPTIONS[colIdx]}
                    isEnabled={enabledChecks[colIdx]}
                    onSpcTogglesChange={onSpcTogglesChange}
                />
            )}
        </tr>
    );
}


function SpcCheckCell({
    positionNumber,
    colIdx,
    spcCheckResult,
    title,
    isEnabled,
    onSpcTogglesChange,
}: {
    positionNumber: string,
    colIdx: number,
    spcCheckResult: boolean | null,
    title: string,
    isEnabled: boolean,
    onSpcTogglesChange: (isEnabled: boolean, colIdx: number, positionNumber?: string) => void,
}) {
    const handleEnabledCheckChanged = (checked: boolean) => {
        onSpcTogglesChange(checked, colIdx, positionNumber);
    };

    const backgroundColor = spcCheckResult == null ? nullResultsBackground
        : spcCheckResult ? 'rgba(226, 158, 11, 0.47)'
            : undefined;

    return (
        <td
            style={{ backgroundColor }}
            title={title}
            onClick={() => handleEnabledCheckChanged(!isEnabled)} // Handles cell click
        >
            <input
                type='checkbox'
                checked={isEnabled}
                onClick={(e) => e.stopPropagation()} // Prevents event bubbling
                onChange={(e) => handleEnabledCheckChanged(e.target.checked)} // Handles checkbox change
            />
        </td>
    );
}
